import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  collection, addDoc, serverTimestamp, Timestamp, onSnapshot,
  query, orderBy, doc, updateDoc, increment, setDoc, deleteDoc, getDoc, where, getDocs, limitToLast, writeBatch,
} from 'firebase/firestore';
import { Smile, Paperclip, Mic, Send, X, Reply, Copy, Trash2, Smile as SmileIcon, Pencil, Pin, Search, ChevronUp, ChevronDown, Square, Users, Clock, Timer, Phone, Video, MoreVertical, Ban, Forward, FileText, CalendarDays } from 'lucide-react';
import EmojiPickerCustom from './EmojiPickerCustom';
import { AttachMenu } from './AttachMenu';
import './EmojiPickerCustom.css';
import { InputLinkPreview, MsgLinkPreview } from './LinkPreview';
import { useLinkPreview } from './UselinkPreview';
import './LinkPreview.css';
import { ReactionPicker } from './ReactionPicker';
import './ReactionPicker.css';
import { GifPicker } from './GifPicker';
import { playMessageSound, playTypingSound } from './sounds';
import type { ChatContact } from './types';
import { VideoCall } from './VideoCall';
import { startCall, startGroupCall } from './Callmanager';
import { useFCM } from './Usefcm';
import './groupcall.css';
import { LocationMap } from './LocationMap';
import { ImageEditor } from './ImageEditor';


// ── Pixel Boot Sequence ──
const PixelBoot = () => {
  const [visible, setVisible] = useState(true);
  const [lines, setLines] = useState<string[]>([]);
  const bootLines = [
    'TOLKX OS v1.0.0',
    'INICIANDO SISTEMA...',
    'CARGANDO MODULOS... OK',
    'CONECTANDO SERVIDOR... OK',
    'VERIFICANDO USUARIO... OK',
    '> BIENVENIDO A TOLKX',
  ];

  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i < bootLines.length) {
        setLines(prev => [...prev, bootLines[i]]);
        i++;
      } else {
        clearInterval(interval);
        setTimeout(() => setVisible(false), 600);
      }
    }, 280);
    return () => clearInterval(interval);
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 500,
      background: '#000', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          fontFamily: "'Press Start 2P', monospace",
          fontSize: '0.7rem', color: i === lines.length - 1 ? '#7c3aed' : '#e9d5ff',
          letterSpacing: '0.1em',
          animation: 'fadeInLine 0.2s ease',
        }}>{line}</div>
      ))}
      <style>{`@keyframes fadeInLine { from { opacity:0; transform: translateX(-10px); } to { opacity:1; transform: translateX(0); } }`}</style>
    </div>
  );
};

interface Props {
  currentUser: any;
  activeChat: ChatContact | null;
  onClose?: () => void;
}



const isEmojiOnly = (text: string) => {
  const emojiRegex = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic}|\s)+$/u;
  return emojiRegex.test(text.trim()) && text.trim().length <= 8;
};

const formatTime = (ts: any) => {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (ts: any) => {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Hoy';
  if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
  return date.toLocaleDateString();
};

const truncate = (str: string, n: number) => str?.length > n ? str.slice(0, n) + '...' : str;

const formatLastSeen = (lastSeen: any): string => {
  if (!lastSeen) return '';
  const date = lastSeen?.toDate ? lastSeen.toDate() : new Date(lastSeen);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (diffMin < 1) return 'ult. vez hace un momento';
  if (diffMin < 60) return `ult. vez hace ${diffMin} min`;
  if (diffHrs < 24) return `ult. vez hoy a las ${timeStr}`;
  if (diffDays === 1) return `ult. vez ayer a las ${timeStr}`;
  return `ult. vez el ${date.toLocaleDateString([], { day: '2-digit', month: 'short' })} a las ${timeStr}`;
};

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const highlightText = (text: string, query: string): React.ReactElement => {
  if (!query.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="msg__highlight">{part}</mark>
          : part
      )}
    </>
  );
};

export const ChatWindow = ({ currentUser, activeChat }: Props): React.ReactElement => {
  const [messages, setMessages] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const oldestDocRef = useRef<any>(null);
  const PAGE_SIZE = 40;       // initial load
  const LOAD_MORE_SIZE = 40;  // when scrolling up
  const [text, setText] = useState('');
  const { preview: linkPreview, loading: linkLoading, dismiss: dismissLink } = useLinkPreview(text);
  const { sendCallNotification } = useFCM(currentUser);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [groupData, setGroupData] = useState<any>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberInput, setAddMemberInput] = useState('');
  const [addMemberResult, setAddMemberResult] = useState<any>(null);
  const [addMemberError, setAddMemberError] = useState('');
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [replyTo, setReplyTo] = useState<any>(null);
  const [contextMenu, setContextMenu] = useState<{ msg: any; x: number; y: number } | null>(null);
  const [reactionPicker, setReactionPicker] = useState<{ msg: any; x: number; y: number } | null>(null);
  const [editingMsg, setEditingMsg] = useState<any>(null);
  const [editText, setEditText] = useState('');
  const [pinnedMsgs, setPinnedMsgs] = useState<any[]>([]);
  const [showPinnedList, setShowPinnedList] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false); // other user recording
  const [audioPreview, setAudioPreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [memberProfiles, setMemberProfiles] = useState<{ [uid: string]: { displayName: string; photoURL?: string } }>({});
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleText, setScheduleText] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledMsgs, setScheduledMsgs] = useState<any[]>([]);
  const [showScheduledList, setShowScheduledList] = useState(false);
  const [activeCall, setActiveCall] = useState<{ type: 'audio' | 'video' } | null>(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [imagePreview, setImagePreview] = useState<{ url: string; file: File } | null>(null);
  const [forwardMsg, setForwardMsg] = useState<any>(null);
  const [showForward, setShowForward] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<any>(null);
  const [contactStatus, setContactStatus] = useState<{ status: string; statusText?: string } | null>(null);
  const [mentionList, setMentionList] = useState<any[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [showEditor, setShowEditor] = useState(false);
  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [locationWatchId, setLocationWatchId] = useState<number | null>(null);
  const [showChatMenu, setShowChatMenu] = useState(false);
  const [showDisappearMenu, setShowDisappearMenu] = useState(false);
  const [disappearTimer, setDisappearTimer] = useState<number | null>(null);
  const [explodingIds, setExplodingIds] = useState<Set<string>>(new Set());
  const [deletingChat, setDeletingChat] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockDocId, setBlockDocId] = useState<string | null>(null);
  const [showAgendaFromMsg, setShowAgendaFromMsg] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isPixelTheme, setIsPixelTheme] = useState(() => localStorage.getItem('chatTheme') === 'pixel');
  const [agendaMsgText, setAgendaMsgText] = useState('');
  const [agendaDate, setAgendaDate] = useState('');
  const [agendaTime, setAgendaTime] = useState('');
  const [agendaSaving, setAgendaSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeout = useRef<any>(null);
  const prevMsgCount = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const msgRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const emojiBtnRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadWhileAway, setUnreadWhileAway] = useState(0);

  const isGroup = activeChat?.isGroup === true;
  const [seenTooltip, setSeenTooltip] = useState<string | null>(null);

  // ── Load member profiles for groups ──
  useEffect(() => {
    if (!activeChat || !isGroup || !activeChat.members) return;
    const fetchProfiles = async () => {
      const profiles: { [uid: string]: { displayName: string; photoURL?: string } } = {};
      for (const uid of activeChat.members!) {
        if (uid === currentUser.uid) continue;
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) {
            const d = snap.data();
            profiles[uid] = { displayName: d.displayName || 'Usuario', photoURL: d.photoURL };
          }
        } catch {}
      }
      setMemberProfiles(profiles);
    };
    fetchProfiles();
  }, [activeChat, isGroup]);

  // ── Load messages (paginated — last 20, real-time) ──
  useEffect(() => {
    if (!activeChat) return;
    prevMsgCount.current = 0;
    oldestDocRef.current = null;
    setHasMore(true);
    setMessages([]);

    // One-time read for deletedAt — no need for realtime listener on chat doc
    let deletedAt: Date | null = null;
    getDoc(doc(db, 'chats', activeChat.chatId)).then(chatSnap => {
      const chatData = chatSnap.data();
      const deletedTs = chatData?.deletedAt?.[currentUser.uid];
      deletedAt = deletedTs?.toDate ? deletedTs.toDate() : null;
    }).catch(() => {});

    // Load last PAGE_SIZE messages in real-time
    const q = query(
      collection(db, 'chats', activeChat.chatId, 'messages'),
      orderBy('createdAt', 'asc'),
      limitToLast(PAGE_SIZE)
    );

    const msgUnsub = onSnapshot(q, (snap) => {
        // Track oldest doc for "load more"
        if (snap.docs.length > 0 && !oldestDocRef.current) {
          oldestDocRef.current = snap.docs[0];
          setHasMore(snap.docs.length === PAGE_SIZE);
        }

        let msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (deletedAt) {
          msgs = msgs.filter((m: any) => {
            const msgDate = m.createdAt?.toDate ? m.createdAt.toDate() : new Date(m.createdAt);
            return deletedAt ? msgDate > deletedAt : true;
          });
        }
        if (prevMsgCount.current > 0 && msgs.length > prevMsgCount.current) {
          const lastMsg = msgs[msgs.length - 1];
          if ((lastMsg as any).senderId !== currentUser.uid) playMessageSound();
        }
        const now = Date.now();
        const filtered = msgs.filter((m: any) => {
          if (m.hiddenFor?.includes(currentUser.uid)) return false;
          if (!m.expiresAt) return true;
          const exp = m.expiresAt?.toMillis?.() || m.expiresAt?.seconds * 1000;
          if (exp < now) {
            deleteDoc(doc(db, 'chats', activeChat.chatId, 'messages', m.id)).catch(() => {});
            return false;
          }
          return true;
        });
        prevMsgCount.current = filtered.length;
        setMessages(prev => {
          // Merge: keep older loaded messages, replace/append real-time ones
          const existingIds = new Set(filtered.map((m: any) => m.id));
          const older = prev.filter((m: any) => !existingIds.has(m.id));
          return [...older, ...filtered];
        });
        // Batch all read/seenBy updates in one go — avoids N individual writes
        const batchUpdates = async () => {
          const batch = writeBatch(db);
          let hasUpdates = false;

          // Reset unread count
          batch.update(doc(db, 'chats', activeChat.chatId), {
            [`unreadCount.${currentUser.uid}`]: 0
          });

          msgs.forEach((m: any) => {
            const updates: any = {};
            if (m.senderId !== currentUser.uid && m.read === false) updates.read = true;
            if (!(m.seenBy || []).includes(currentUser.uid)) {
              updates.seenBy = [...(m.seenBy || []), currentUser.uid];
            }
            if (Object.keys(updates).length > 0) {
              batch.update(doc(db, 'chats', activeChat.chatId, 'messages', m.id), updates);
              hasUpdates = true;
            }
          });

          if (hasUpdates) batch.commit().catch(() => {});
        };
        batchUpdates();
    });

    return () => msgUnsub();
  }, [activeChat, currentUser.uid]);

  // ── Load more (older) messages on scroll up ──
  const loadMoreMessages = async () => {
    if (!activeChat || loadingMore || !hasMore || !oldestDocRef.current) return;
    setLoadingMore(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;

    try {
      const { endBefore } = await import('firebase/firestore');
      const olderQ = query(
        collection(db, 'chats', activeChat.chatId, 'messages'),
        orderBy('createdAt', 'asc'),
        endBefore(oldestDocRef.current),
        limitToLast(LOAD_MORE_SIZE)
      );
      const snap = await getDocs(olderQ);
      if (snap.empty) { setHasMore(false); return; }

      oldestDocRef.current = snap.docs[0];
      setHasMore(snap.docs.length === LOAD_MORE_SIZE);

      const older = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMessages(prev => [...older, ...prev]);

      // Restore scroll position so user stays at same spot
      requestAnimationFrame(() => {
        if (container) container.scrollTop = container.scrollHeight - prevScrollHeight;
      });
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  // ── Pinned message ──
  useEffect(() => {
    if (!activeChat) return;
    const unsub = onSnapshot(doc(db, 'chats', activeChat.chatId), (snap) => {
      if (snap.exists()) {
        setPinnedMsgs(snap.data()?.pinnedMessages || []);
        setDisappearTimer(snap.data()?.disappearTimer || null);
      }
    });
    return () => unsub();
  }, [activeChat]);

  // ── Load group data ──
  useEffect(() => {
    if (!activeChat || !isGroup) return;
    const unsub = onSnapshot(doc(db, 'chats', activeChat.chatId), (snap) => {
      if (snap.exists()) setGroupData(snap.data());
    });
    return () => unsub();
  }, [activeChat, isGroup]);

  // ── Group typing listener ──
  useEffect(() => {
    if (!activeChat || !isGroup) return;
    const typingCol = collection(db, 'chats', activeChat.chatId, 'typing');
    const unsub = onSnapshot(typingCol, (snap) => {
      const names: string[] = [];
      snap.docs.forEach(d => {
        if (d.id !== currentUser.uid && d.data()?.isTyping) {
          const profile = memberProfiles[d.id];
          names.push(profile?.displayName || 'Alguien');
        }
      });
      setTypingUsers(names);
    });
    return () => unsub();
  }, [activeChat, isGroup, memberProfiles]);

  // ── Contact status listener ──
  const [contactHideLastSeen, setContactHideLastSeen] = useState(false);
  const [contactHideOnline, setContactHideOnline] = useState(false);
  const [contactOnline, setContactOnline] = useState(false);
  const [contactLastSeen, setContactLastSeen] = useState<any>(null);
  useEffect(() => {
    if (!activeChat || isGroup) return;
    setContactOnline(activeChat.online ?? false);
    setContactLastSeen(activeChat.lastSeen ?? null);
    const unsub = onSnapshot(doc(db, 'users', activeChat.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setContactStatus({ status: data.status || 'online', statusText: data.statusText || '' });
        setContactHideLastSeen(data.hideLastSeen ?? false);
        setContactHideOnline(data.hideOnlineStatus ?? false);
        setContactOnline(data.online ?? false);
        setContactLastSeen(data.lastSeen ?? null);
      }
    });
    return () => unsub();
  }, [activeChat, isGroup]);

  // ── Typing listener ──
  useEffect(() => {
    if (!activeChat || isGroup) return;
    const typingRef = doc(db, 'chats', activeChat.chatId, 'typing', activeChat.uid);
    const unsub = onSnapshot(typingRef, (snap) => {
      if (snap.exists()) {
        const typing = snap.data()?.isTyping === true;
        if (typing) playTypingSound();
        setIsTyping(typing);
      } else setIsTyping(false);
    });
    return () => unsub();
  }, [activeChat, isGroup]);

  // ── Recording listener ──
  useEffect(() => {
    if (!activeChat || isGroup) return;
    const typingRef = doc(db, 'chats', activeChat.chatId, 'typing', activeChat.uid);
    const unsub = onSnapshot(typingRef, (snap) => {
      if (snap.exists()) {
        setIsRecording(snap.data()?.isRecording === true);
      } else setIsRecording(false);
    });
    return () => unsub();
  }, [activeChat, isGroup]);

  // ── Load scheduled messages ──
  useEffect(() => {
    if (!activeChat) return;
    const q = query(
      collection(db, 'scheduledMessages'),
      where('chatId', '==', activeChat.chatId),
      where('senderId', '==', currentUser.uid),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      setScheduledMsgs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [activeChat, currentUser.uid]);

  // ── Delete expired messages every 5 seconds ──
  useEffect(() => {
    if (!activeChat) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setMessages(prev => {
        const expired = prev.filter((m: any) => {
          if (!m.expiresAt) return false;
          const exp = m.expiresAt?.toMillis?.() || (m.expiresAt?.seconds || 0) * 1000;
          return exp <= now;
        });
        if (expired.length > 0) {
          // Trigger explosion animation
          setExplodingIds(new Set(expired.map((m: any) => m.id)));
          setTimeout(() => {
            expired.forEach((m: any) => {
              deleteDoc(doc(db, 'chats', activeChat.chatId, 'messages', m.id)).catch(() => {});
            });
            setExplodingIds(new Set());
          }, 600);
        }
        return prev.filter((m: any) => {
          if (!m.expiresAt) return true;
          const exp = m.expiresAt?.toMillis?.() || (m.expiresAt?.seconds || 0) * 1000;
          return exp > now || explodingIds.has(m.id);
        });
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [activeChat]);

  // ── Check and send scheduled messages every 30 seconds ──
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = new Date();
      for (const msg of scheduledMsgs) {
        const scheduledAt = msg.scheduledAt?.toDate ? msg.scheduledAt.toDate() : new Date(msg.scheduledAt);
        if (scheduledAt <= now) {
          try {
            await addDoc(collection(db, 'chats', msg.chatId, 'messages'), {
              senderId: msg.senderId,
              senderName: msg.senderName,
              senderPhoto: msg.senderPhoto || null,
              type: 'text',
              text: msg.text,
              createdAt: serverTimestamp(),
              read: false,
              scheduled: true,
            });
            await updateDoc(doc(db, 'chats', msg.chatId), {
              lastMessage: msg.text,
              lastMessageTime: serverTimestamp(),
              [`unreadCount.${msg.receiverId}`]: increment(1),
            });
            await updateDoc(doc(db, 'scheduledMessages', msg.id), { status: 'sent' });
          } catch (e) { console.error(e); }
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [scheduledMsgs]);

  // ── Scroll to bottom on chat change ──
  useEffect(() => {
    if (!activeChat) return;
    setIsAtBottom(true);
    setUnreadWhileAway(0);
    // Varios intentos para asegurar que el DOM ya renderizó los mensajes
    const attempts = [0, 50, 150, 300, 500];
    const timers = attempts.map(ms =>
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [activeChat?.chatId]);

  // ── Smart scroll: solo si el usuario está abajo ──
  useEffect(() => {
    if (showSearch) return;
    if (isAtBottom) {
      const container = messagesContainerRef.current;
      if (container) {
        const dist = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (dist > 1200) {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }
      setUnreadWhileAway(0);
    } else {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg && lastMsg.senderId !== currentUser.uid) {
        setUnreadWhileAway(prev => prev + 1);
      }
    }
  }, [messages, isTyping]);

  // ── Detectar si el usuario está al fondo ──
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 80;
    setIsAtBottom(atBottom);
    if (atBottom) setUnreadWhileAway(0);
    // Load more when near top
    if (container.scrollTop < 300 && hasMore && !loadingMore) {
      loadMoreMessages();
    }
  };

  // ── visualViewport: keep chat-window anchored to visible area when keyboard opens ──
  const chatWindowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeChat) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const onResize = () => {
      const el = chatWindowRef.current;
      if (!el) return;
      // Offset from top of page to visible viewport top
      const offsetTop = viewport.offsetTop || 0;
      const height = viewport.height;
      el.style.top = `${offsetTop}px`;
      el.style.height = `${height}px`;
      // Scroll to bottom when keyboard opens
      if (isAtBottom) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'instant' }), 50);
      }
    };

    const onScroll = () => onResize();

    viewport.addEventListener('resize', onResize);
    viewport.addEventListener('scroll', onScroll);
    onResize(); // run once on mount

    return () => {
      viewport.removeEventListener('resize', onResize);
      viewport.removeEventListener('scroll', onScroll);
      // Reset styles on unmount
      if (chatWindowRef.current) {
        chatWindowRef.current.style.top = '';
        chatWindowRef.current.style.height = '';
      }
    };
  }, [activeChat, isAtBottom]);

  // ── Cerrar emoji picker: manejado por emoji-picker-overlay en el JSX ──

  // ── Incoming calls handled globally in chat.tsx ──

  // ── Check block status ──
  useEffect(() => {
    if (!activeChat || isGroup) return;
    const id1 = `${currentUser.uid}_${activeChat.uid}`;
    const id2 = `${activeChat.uid}_${currentUser.uid}`;
    const checkBlock = async () => {
      const [s1, s2] = await Promise.all([getDoc(doc(db, 'friends', id1)), getDoc(doc(db, 'friends', id2))]);
      if (s1.exists() && s1.data()?.status === 'blocked' && s1.data()?.blockedBy === currentUser.uid) {
        setIsBlocked(true); setBlockDocId(id1);
      } else if (s2.exists() && s2.data()?.status === 'blocked' && s2.data()?.blockedBy === currentUser.uid) {
        setIsBlocked(true); setBlockDocId(id2);
      } else {
        setIsBlocked(false); setBlockDocId(null);
      }
    };
    checkBlock();
  }, [activeChat, currentUser.uid, isGroup]);

  // ── Close menus on outside click ──
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) setContextMenu(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Search ──
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); setSearchIndex(0); return; }
    const results = messages.filter(m => m.type === 'text' && m.text?.toLowerCase().includes(searchQuery.toLowerCase())).map(m => m.id);
    setSearchResults(results);
    setSearchIndex(0);
    if (results.length > 0) scrollToMsg(results[0]);
  }, [searchQuery, messages]);

  useEffect(() => {
    if (searchResults.length > 0) scrollToMsg(searchResults[searchIndex]);
  }, [searchIndex]);

  useEffect(() => {
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 100);
    else { setSearchQuery(''); setSearchResults([]); }
  }, [showSearch]);

  const scrollToMsg = (id: string) => {
    const el = msgRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSearchNav = (dir: 'up' | 'down') => {
    if (searchResults.length === 0) return;
    setSearchIndex(prev => dir === 'down' ? (prev + 1) % searchResults.length : (prev - 1 + searchResults.length) % searchResults.length);
  };

  // ── Typing status ──
  const isTypingRef = useRef(false);
  const handleTextChange = (val: string) => {
    setText(val);
    if (!activeChat) return;

    const typingRef = doc(db, 'chats', activeChat.chatId, 'typing', currentUser.uid);

    // Solo llama a Firestore si no estaba ya en typing — evita llamada por cada tecla
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      setDoc(typingRef, { isTyping: true }, { merge: true }).catch(() => {});
    }

    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      isTypingRef.current = false;
      setDoc(typingRef, { isTyping: false }, { merge: true }).catch(() => {});
    }, 2000);

    if (isGroup) {
      const atMatch = val.match(/@(\w*)$/);
      if (atMatch) {
        const search = atMatch[1].toLowerCase();
        const members = Object.entries(memberProfiles)
          .map(([uid, p]) => ({ uid, ...p }))
          .filter(m => m.uid !== currentUser.uid && (m.displayName?.toLowerCase().includes(search) || ''));
        setMentionList(members);
        setShowMentions(members.length > 0);
      } else {
        setShowMentions(false);
        setMentionList([]);
      }
    }
  };

  const handleSelectMention = (member: any) => {
    const newText = text.replace(/@\w*$/, `@${member.displayName} `);
    setText(newText);
    setShowMentions(false);
    setMentionList([]);
    textareaRef.current?.focus();
  };

  // ── Schedule message ──
  const handleScheduleMessage = async () => {
    if (!scheduleText.trim() || !scheduleDate || !scheduleTime || !activeChat) return;
    const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
    if (scheduledAt <= new Date()) { alert('La fecha debe ser en el futuro.'); return; }

    try {
      await addDoc(collection(db, 'scheduledMessages'), {
        chatId: activeChat.chatId,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null,
        receiverId: isGroup ? '' : activeChat.uid,
        text: scheduleText.trim(),
        scheduledAt,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setShowSchedule(false);
      setScheduleText('');
      setScheduleDate('');
      setScheduleTime('');
    } catch (e) { console.error(e); }
  };

  const handleDeleteScheduled = async (id: string) => {
    await deleteDoc(doc(db, 'scheduledMessages', id));
  };

  // ── Voice Recording ──
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioPreview({ url, blob });
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      // Notify other user
      if (activeChat) setDoc(doc(db, 'chats', activeChat.chatId, 'typing', currentUser.uid), { isRecording: true }, { merge: true });
    } catch (e) { console.error('Mic error:', e); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    if (activeChat) setDoc(doc(db, 'chats', activeChat.chatId, 'typing', currentUser.uid), { isRecording: false }, { merge: true });
  };
  const cancelRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setAudioPreview(null);
    audioChunksRef.current = [];
    if (activeChat) setDoc(doc(db, 'chats', activeChat.chatId, 'typing', currentUser.uid), { isRecording: false }, { merge: true });
  };

  const sendVoiceMessage = async () => {
    if (!audioPreview || !activeChat) return;
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
          senderId: currentUser.uid, senderName: currentUser.displayName || 'Tú',
          type: 'audio', audioData: base64, duration: recordingTime, createdAt: serverTimestamp(), read: false,
        });
        await updateDoc(doc(db, 'chats', activeChat.chatId), {
          lastMessage: '🎤 Audio', lastMessageTime: serverTimestamp(),
          ...(isGroup
            ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
            : { [`unreadCount.${activeChat.uid}`]: increment(1) }),
        });
        setAudioPreview(null); setRecordingTime(0);
      };
      reader.readAsDataURL(audioPreview.blob);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  // ── Context menu ──
  const handleContextMenu = (e: React.MouseEvent, msg: any) => {
    e.preventDefault(); e.stopPropagation();
    const menuW = 210;
    const menuH = 320; // max height estimate
    let x = e.clientX;
    let y = e.clientY;
    // Adjust horizontal
    if (x + menuW > window.innerWidth) x = window.innerWidth - menuW - 8;
    if (x < 8) x = 8;
    // Adjust vertical — if too close to bottom, show above click point
    if (y + menuH > window.innerHeight) y = Math.max(8, window.innerHeight - menuH - 8);
    if (y < 8) y = 8;
    setContextMenu({ msg, x, y });
    setReactionPicker(null); setShowEmoji(false); setShowGif(false);
  };

  // ── Reset UI state when switching chats ──
  useEffect(() => {
    setShowEmoji(false);
    setShowGif(false);
    setShowAttachMenu(false);
    setShowSchedule(false);
    setShowScheduledList(false);
    setShowChatMenu(false);
    setContextMenu(null);
    setReactionPicker(null);
  }, [activeChat?.chatId]);

  // ── Reset UI state when switching chats ──
  useEffect(() => {
    setShowEmoji(false);
    setShowGif(false);
    setShowAttachMenu(false);
    setShowSchedule(false);
    setShowScheduledList(false);
    setShowChatMenu(false);
    setContextMenu(null);
    setReactionPicker(null);
  }, [activeChat?.chatId]);

  // Watch pixel theme
  useEffect(() => {
    const check = () => setIsPixelTheme(document.documentElement.getAttribute('data-chat-theme') === 'pixel');
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-chat-theme'] });
    return () => observer.disconnect();
  }, []);



  const handleMenuAgenda = () => {
    const text = contextMenu?.msg?.text?.slice(0, 80) || 'Evento';
    setAgendaMsgText(text);
    setAgendaDate('');
    setAgendaTime('');
    setShowAgendaFromMsg(true);
    setContextMenu(null);
  };

  const handleSaveAgendaFromMsg = async () => {
    if (!agendaMsgText.trim() || !agendaDate || !agendaTime) return;
    setAgendaSaving(true);
    try {
      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
      const dateTime = new Date(`${agendaDate}T${agendaTime}`);
      await addDoc(collection(db, 'agenda'), {
        uid: currentUser.uid,
        title: agendaMsgText.trim(),
        note: activeChat ? `Desde chat con ${activeChat.displayName}` : '',
        dateTime,
        createdAt: serverTimestamp(),
        done: false,
      });
      setShowAgendaFromMsg(false);
    } catch(e) { console.error(e); }
    finally { setAgendaSaving(false); }
  };

  const handleMenuReply = () => { setReplyTo(contextMenu!.msg); setContextMenu(null); textareaRef.current?.focus(); };
  const handleMenuCopy = () => { navigator.clipboard.writeText(contextMenu!.msg.text || ''); setCopied(true); setTimeout(() => setCopied(false), 2000); setContextMenu(null); };
  const handleMenuEdit = () => { setEditingMsg(contextMenu!.msg); setEditText(contextMenu!.msg.text || ''); setContextMenu(null); };
  const handleMenuDelete = async () => {
    if (!activeChat) return;
    await updateDoc(doc(db, 'chats', activeChat.chatId, 'messages', contextMenu!.msg.id), {
      deleted: true,
      text: '',
      imageUrl: null,
      fileUrl: null,
    });
    setContextMenu(null);
  };
  const handleMenuReact = () => { const { msg, x, y } = contextMenu!; setContextMenu(null); setReactionPicker({ msg, x, y: y - 60 }); };
  const handleMenuForward = () => { setForwardMsg(contextMenu!.msg); setShowForward(true); setContextMenu(null); };

  const handleConfirmForward = async (targetChat: any) => {
    if (!forwardMsg) return;
    try {
      const fwd: any = {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null,
        type: forwardMsg.type,
        forwarded: true,
        forwardedFrom: forwardMsg.senderName || 'Usuario',
        createdAt: serverTimestamp(),
        read: false,
      };
      if (forwardMsg.type === 'text') fwd.text = forwardMsg.text;
      if (forwardMsg.type === 'image') fwd.imageUrl = forwardMsg.imageUrl;
      if (forwardMsg.type === 'audio') { fwd.audioUrl = forwardMsg.audioUrl; fwd.duration = forwardMsg.duration; }
      if (forwardMsg.type === 'gif') fwd.gifUrl = forwardMsg.gifUrl;
      if (forwardMsg.type === 'location') { fwd.lat = forwardMsg.lat; fwd.lng = forwardMsg.lng; }

      await addDoc(collection(db, 'chats', targetChat.chatId, 'messages'), fwd);
      await updateDoc(doc(db, 'chats', targetChat.chatId), {
        lastMessage: forwardMsg.type === 'text' ? forwardMsg.text : `📎 ${forwardMsg.type}`,
        lastMessageTime: serverTimestamp(),
      });
      setShowForward(false);
      setForwardMsg(null);
    } catch (e) { console.error(e); }
  };

  const handleSaveEdit = async () => {
    if (!activeChat || !editingMsg || !editText.trim()) return;
    await updateDoc(doc(db, 'chats', activeChat.chatId, 'messages', editingMsg.id), { text: editText.trim(), edited: true });
    setEditingMsg(null); setEditText('');
  };

  const handleMenuPin = async () => {
    if (!activeChat || !contextMenu) return;
    const msg = contextMenu.msg;
    const isAlreadyPinned = pinnedMsgs.some((p: any) => p.id === msg.id);
    const newPinned = isAlreadyPinned
      ? pinnedMsgs.filter((p: any) => p.id !== msg.id)
      : [...pinnedMsgs, { id: msg.id, text: msg.text || (msg.type === 'gif' ? '🎬 GIF' : msg.type === 'audio' ? '🎤 Audio' : msg.type === 'image' ? '📷 Imagen' : '📎'), senderId: msg.senderId, type: msg.type }];
    await updateDoc(doc(db, 'chats', activeChat.chatId), { pinnedMessages: newPinned });
    setContextMenu(null);
  };

  const scrollToPinned = (id?: string) => {
    const targetId = id || (pinnedMsgs.length > 0 ? pinnedMsgs[pinnedMsgs.length - 1].id : null);
    if (!targetId) return;
    const el = msgRefs.current[targetId];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('msg--highlight'); setTimeout(() => el.classList.remove('msg--highlight'), 1500); }
    setShowPinnedList(false);
  };

  const handleReaction = async (msg: any, emoji: string) => {
    if (!activeChat) return;
    setReactionPicker(null);
    const msgRef = doc(db, 'chats', activeChat.chatId, 'messages', msg.id);
    const reactions = msg.reactions || {};
    const emojiReactions: string[] = reactions[emoji] || [];
    const alreadyReacted = emojiReactions.includes(currentUser.uid);
    const updatedEmoji = alreadyReacted ? emojiReactions.filter((uid: string) => uid !== currentUser.uid) : [...emojiReactions, currentUser.uid];
    const updatedReactions = { ...reactions, [emoji]: updatedEmoji };
    Object.keys(updatedReactions).forEach(key => { if (updatedReactions[key].length === 0) delete updatedReactions[key]; });
    await updateDoc(msgRef, { reactions: updatedReactions });
  };

  const handleBlock = async () => {
    if (!activeChat || isGroup) return;
    setShowChatMenu(false);
    const id = `${currentUser.uid}_${activeChat.uid}`;
    const ref = doc(db, 'friends', id);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, { status: 'blocked', blockedBy: currentUser.uid });
      setBlockDocId(id);
    } else {
      await setDoc(ref, {
        fromUid: currentUser.uid, toUid: activeChat.uid,
        status: 'blocked', blockedBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      setBlockDocId(id);
    }
    setIsBlocked(true);
  };

  const handleUnblock = async () => {
    if (!blockDocId) return;
    setShowChatMenu(false);
    await updateDoc(doc(db, 'friends', blockDocId), { status: 'accepted', blockedBy: null });
    setIsBlocked(false); setBlockDocId(null);
  };

  const handleDeleteChat = async () => {
    if (!activeChat) return;
    const confirmed = window.confirm('¿Borrar el historial de este chat? Solo se borrará para ti.');
    if (!confirmed) return;
    setDeletingChat(true);
    setShowChatMenu(false);
    try {
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        [`deletedAt.${currentUser.uid}`]: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
    finally { setDeletingChat(false); }
  };

  const isAdmin = isGroup && (activeChat?.admins || groupData?.admins || []).includes(currentUser.uid);

  const handleLeaveGroup = async () => {
    if (!activeChat) return;
    const confirmed = window.confirm('¿Seguro que quieres salir del grupo?');
    if (!confirmed) return;
    try {
      const newMembers = (activeChat.members || []).filter((uid: string) => uid !== currentUser.uid);
      const newAdmins = (groupData?.admins || []).filter((uid: string) => uid !== currentUser.uid);
      const finalAdmins = newAdmins.length === 0 && newMembers.length > 0 ? [newMembers[0]] : newAdmins;
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        members: newMembers,
        admins: finalAdmins,
      });
      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: 'system',
        type: 'system',
        text: `${currentUser.displayName || 'Usuario'} salió del grupo`,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
  };

  const handleSearchAddMember = async () => {
    if (!addMemberInput.trim()) return;
    setAddMemberLoading(true);
    setAddMemberError('');
    setAddMemberResult(null);
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('username', '==', addMemberInput.trim().toLowerCase())));
      if (snap.empty) { setAddMemberError('Usuario no encontrado.'); return; }
      const user = { uid: snap.docs[0].id, ...snap.docs[0].data() };
      if ((activeChat?.members || []).includes((user as any).uid)) { setAddMemberError('Ya es miembro del grupo.'); return; }
      setAddMemberResult(user);
    } catch (e) { setAddMemberError('Error al buscar.'); }
    finally { setAddMemberLoading(false); }
  };

  const handleConfirmAddMember = async () => {
    if (!addMemberResult || !activeChat) return;
    try {
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        members: [...(activeChat.members || []), addMemberResult.uid],
        [`memberProfiles.${addMemberResult.uid}`]: { displayName: addMemberResult.displayName || addMemberResult.username, photoURL: addMemberResult.photoURL || null },
      });
      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: 'system', type: 'system',
        text: `${addMemberResult.displayName || addMemberResult.username} fue añadido al grupo`,
        createdAt: serverTimestamp(),
      });
      setAddMemberInput('');
      setAddMemberResult(null);
      setShowAddMember(false);
    } catch (e) { console.error(e); }
  };

  const handleToggleAdmin = async (uid: string) => {
    if (!activeChat || !isAdmin) return;
    const admins: string[] = groupData?.admins || [];
    const newAdmins = admins.includes(uid) ? admins.filter(a => a !== uid) : [...admins, uid];
    await updateDoc(doc(db, 'chats', activeChat.chatId), { admins: newAdmins });
  };

  const handleRemoveMember = async (uid: string) => {
    if (!activeChat || !isAdmin) return;
    const confirmed = window.confirm('¿Eliminar este miembro del grupo?');
    if (!confirmed) return;
    const newMembers = (activeChat.members || []).filter((m: string) => m !== uid);
    const newAdmins = (groupData?.admins || []).filter((a: string) => a !== uid);
    await updateDoc(doc(db, 'chats', activeChat.chatId), { members: newMembers, admins: newAdmins });
    await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
      senderId: 'system', type: 'system',
      text: `${memberProfiles[uid]?.displayName || 'Usuario'} fue removido del grupo`,
      createdAt: serverTimestamp(),
    });
  };

  const handleGroupPhotoUpload = async (file: File) => {
    if (!activeChat || !isAdmin) return;
    try {
      const compressed = await new Promise<Blob>((resolve, reject) => {
        const img = document.createElement('img') as HTMLImageElement;
        img.onload = () => {
          const MAX = 400;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else { width = Math.round(width * MAX / height); height = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width; canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('fail')), 'image/jpeg', 0.8);
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
      const storageRef = ref(storage, `group-photos/${activeChat.chatId}/${Date.now()}.jpg`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'chats', activeChat.chatId), { groupPhoto: url });
    } catch (e) { console.error('Group photo upload error:', e); }
  };

  const handleShareLocation = async () => {
    if (!activeChat) return;
    setShowAttachMenu(false);
    if (sharingLocation) {
      if (locationWatchId !== null) navigator.geolocation.clearWatch(locationWatchId);
      setSharingLocation(false);
      setLocationWatchId(null);
      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null,
        type: 'location_end',
        text: '📍 Dejó de compartir ubicación',
        createdAt: serverTimestamp(),
        read: false,
      });
      return;
    }
    if (!navigator.geolocation) { alert('Tu navegador no soporta geolocalización.'); return; }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      const msgRef = await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null,
        type: 'location',
        lat: latitude,
        lng: longitude,
        createdAt: serverTimestamp(),
        read: false,
        live: true,
      });
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        lastMessage: '📍 Ubicación en tiempo real',
        lastMessageTime: serverTimestamp(),
        ...(isGroup
          ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
          : { [`unreadCount.${activeChat.uid}`]: increment(1) }),
      });
      setSharingLocation(true);
      const watchId = navigator.geolocation.watchPosition((p) => {
        updateDoc(msgRef, { lat: p.coords.latitude, lng: p.coords.longitude, updatedAt: serverTimestamp() });
      }, null, { enableHighAccuracy: true, maximumAge: 5000 });
      setLocationWatchId(watchId);
    }, () => alert('No se pudo obtener tu ubicación.'));
  };

  const sendMessage = async (type: 'text' | 'gif' | 'image', content: string, lp?: { url: string; title: string; description: string; image: string; siteName: string }) => {
    if (!activeChat || !content.trim()) return;
    setSending(true);
    if (!isGroup) {
      const typingRef = doc(db, 'chats', activeChat.chatId, 'typing', currentUser.uid);
      await setDoc(typingRef, { isTyping: false }, { merge: true });
      clearTimeout(typingTimeout.current);
    }
    try {
      const msgData: any = {
        senderId: currentUser.uid, senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null, type, createdAt: serverTimestamp(), read: false,
      };
      if (type === 'text') msgData.text = content;
      else if (type === 'gif') msgData.gifUrl = content;
      else if (type === 'image') msgData.imageUrl = content;
      if (replyTo) msgData.replyTo = { id: replyTo.id, text: replyTo.text || (replyTo.type === 'gif' ? '🎬 GIF' : '📷 Imagen'), senderId: replyTo.senderId, senderName: replyTo.senderName, type: replyTo.type };
      if (disappearTimer) msgData.expiresAt = Timestamp.fromMillis(Date.now() + disappearTimer * 1000);
      if (lp) msgData.linkPreview = lp;

      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), msgData);
      const unreadUpdate = isGroup
        ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
        : { [`unreadCount.${activeChat.uid}`]: increment(1) };
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        lastMessage: type === 'text' ? content : type === 'gif' ? '🎬 GIF' : '📷 Imagen',
        lastMessageTime: serverTimestamp(), ...unreadUpdate,
      });
      setText(''); setReplyTo(null); dismissLink();
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleDocUpload = async (file: File) => {
    if (!activeChat) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) { alert('El archivo no puede superar 10MB.'); return; }
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
          senderId: currentUser.uid,
          senderName: currentUser.displayName || 'Tú',
          senderPhoto: currentUser.photoURL || null,
          type: 'file',
          fileData: base64,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          createdAt: serverTimestamp(),
          read: false,
          ...(disappearTimer ? { expiresAt: Timestamp.fromMillis(Date.now() + disappearTimer * 1000) } : {}),
        });
        await updateDoc(doc(db, 'chats', activeChat.chatId), {
          lastMessage: `📎 ${file.name}`,
          lastMessageTime: serverTimestamp(),
          ...(isGroup
            ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
            : { [`unreadCount.${activeChat.uid}`]: increment(1) }),
        });
      };
      reader.readAsDataURL(file);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleSendPoll = async () => {
    if (!activeChat || !pollQuestion.trim()) return;
    const validOptions = pollOptions.filter(o => o.trim());
    if (validOptions.length < 2) { alert('Agrega al menos 2 opciones.'); return; }
    setSending(true);
    try {
      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: currentUser.uid,
        senderName: currentUser.displayName || 'Tú',
        senderPhoto: currentUser.photoURL || null,
        type: 'poll',
        pollQuestion: pollQuestion.trim(),
        pollOptions: validOptions.map(o => ({ text: o.trim(), votes: [] })),
        createdAt: serverTimestamp(),
        read: false,
      });
      await updateDoc(doc(db, 'chats', activeChat.chatId), {
        lastMessage: `📊 ${pollQuestion.trim()}`,
        lastMessageTime: serverTimestamp(),
        ...(isGroup
          ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
          : { [`unreadCount.${activeChat.uid}`]: increment(1) }),
      });
      setPollQuestion('');
      setPollOptions(['', '']);
      setShowPollCreator(false);
    } catch(e) { console.error(e); }
    finally { setSending(false); }
  };

  const handlePollVote = async (msg: any, optionIndex: number) => {
    if (!activeChat) return;
    const options = msg.pollOptions.map((opt: any, i: number) => ({
      ...opt,
      votes: i === optionIndex
        ? opt.votes.includes(currentUser.uid) ? opt.votes : [...opt.votes, currentUser.uid]
        : opt.votes.filter((uid: string) => uid !== currentUser.uid),
    }));
    await updateDoc(doc(db, 'chats', activeChat.chatId, 'messages', msg.id), { pollOptions: options });
  };

  const handleSend = () => { if (text.trim()) sendMessage('text', text.trim(), linkPreview ?? undefined); };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === 'Escape') { setReplyTo(null); setContextMenu(null); setReactionPicker(null); setEditingMsg(null); setShowSearch(false); setShowSchedule(false); }
  };

  // ── Paste image from clipboard ──
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) handleImageUpload(file);
    }
  };

  // ── Format helpers (bold / italic) ──
  const applyFormat = (tag: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = text.slice(start, end);
    const wrapped = selected ? `${tag}${selected}${tag}` : `${tag}${tag}`;
    const newText = text.slice(0, start) + wrapped + text.slice(end);
    setText(newText);
    setTimeout(() => {
      ta.focus();
      const pos = selected ? start + wrapped.length : start + tag.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setEditorSrc(dataUrl);
      setShowEditor(true);
      setShowAttachMenu(false);
    };
    reader.readAsDataURL(file);
  };

  const handleEditorConfirm = (editedUrl: string) => {
    setImagePreview({ url: editedUrl, file: new File([], 'edited.jpg') });
    setShowEditor(false);
    setEditorSrc(null);
  };

  const handleConfirmImageSend = async () => {
    if (!imagePreview || !activeChat) return;
    setUploadingImage(true);
    try {
      let imageUrl = imagePreview.url;
      if (imagePreview.url.startsWith('data:')) {
        // ── Compress before upload ──
        const compressed = await new Promise<Blob>((resolve, reject) => {
          const img = document.createElement('img') as HTMLImageElement;
          img.onload = () => {
            const MAX = 1200;
            let { width, height } = img;
            if (width > MAX || height > MAX) {
              if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
              else { width = Math.round(width * MAX / height); height = MAX; }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
            canvas.toBlob(b => b ? resolve(b) : reject(new Error('Compression failed')), 'image/jpeg', 0.75);
          };
          img.onerror = reject;
          img.src = imagePreview.url;
        });
        const fileName = `chat-images/${activeChat.chatId}/${Date.now()}.jpg`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, compressed);
        imageUrl = await getDownloadURL(storageRef);
      }

      const unreadUpdate = isGroup
        ? Object.fromEntries((activeChat.members || []).filter((uid: string) => uid !== currentUser.uid).map((uid: string) => [`unreadCount.${uid}`, increment(1)]))
        : { [`unreadCount.${activeChat.uid}`]: increment(1) };

      await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
        senderId: currentUser.uid, senderName: currentUser.displayName || 'Tú', senderPhoto: currentUser.photoURL || null,
        type: 'image', imageUrl, createdAt: serverTimestamp(), read: false,
        ...(replyTo ? { replyTo: { id: replyTo.id, text: replyTo.text || '📷 Imagen', senderId: replyTo.senderId, type: replyTo.type } } : {}),
      });
      await updateDoc(doc(db, 'chats', activeChat.chatId), { lastMessage: '📷 Imagen', lastMessageTime: serverTimestamp(), ...unreadUpdate });
      setReplyTo(null);
      setImagePreview(null);
    } catch(e) {
      console.error('Error uploading image:', e);
    } finally {
      setUploadingImage(false);
    }
  };

  const getInitials = (name: string) => (name || 'U').slice(0, 2).toUpperCase();

  const getStatusDot = (status: string) => {
    const colors: Record<string, string> = { online: '#22c55e', busy: '#f59e0b', dnd: '#ef4444', offline: '#71717a' };
    return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: colors[status] || '#22c55e', marginRight: 4 }} />;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { online: 'En línea', busy: 'Ocupado', dnd: 'No molestar', offline: 'Invisible' };
    return labels[status] || 'En línea';
  };

  const renderText = (text: string, _members?: { [uid: string]: { displayName: string; photoURL?: string } }): React.ReactNode => {
    if (!text) return null;

    // First apply inline formatting (bold, italic, strikethrough, code)
    const applyInlineFormat = (str: string, keyOffset: number): React.ReactNode[] => {
      const parts: React.ReactNode[] = [];
      const fmt = /(\*([^*]+)\*|_([^_]+)_|~([^~]+)~|`([^`]+)`)/g;
      let last = 0; let m; let k = keyOffset;
      while ((m = fmt.exec(str)) !== null) {
        if (m.index > last) parts.push(<span key={k++}>{str.slice(last, m.index)}</span>);
        if (m[2]) parts.push(<strong key={k++} className="msg__bold">{m[2]}</strong>);
        else if (m[3]) parts.push(<em key={k++} className="msg__italic">{m[3]}</em>);
        else if (m[4]) parts.push(<span key={k++} className="msg__strike">{m[4]}</span>);
        else if (m[5]) parts.push(<code key={k++} className="msg__code">{m[5]}</code>);
        last = m.index + m[0].length;
      }
      if (last < str.length) parts.push(<span key={k++}>{str.slice(last)}</span>);
      return parts;
    };

    const parts: React.ReactNode[] = [];
    let key = 0;
    const combined = /(@[\w ]+|https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|\+?[0-9][0-9\s\-(). ]{7,}[0-9]|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?)/g;
    let lastIndex = 0;
    let match;

    while ((match = combined.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(...applyInlineFormat(text.slice(lastIndex, match.index), key));
        key += 20;
      }
      const token = match[0];
      if (token.startsWith('@')) {
        parts.push(<span key={key++} className="msg__mention" title={token.slice(1).trim()}>{token}</span>);
      } else if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(token)) {
        parts.push(<a key={key++} href={`mailto:${token}`} className="msg__link" target="_blank" rel="noopener noreferrer">{token}</a>);
      } else if (/^\+?[0-9][0-9\s\-(). ]{7,}[0-9]$/.test(token)) {
        parts.push(<a key={key++} href={`tel:${token.replace(/\s/g, '')}`} className="msg__link">{token}</a>);
      } else {
        const href = token.startsWith('http') ? token : `https://${token}`;
        parts.push(<a key={key++} href={href} className="msg__link" target="_blank" rel="noopener noreferrer">{token}</a>);
      }
      lastIndex = match.index + token.length;
    }
    if (lastIndex < text.length) {
      parts.push(...applyInlineFormat(text.slice(lastIndex), key));
    }
    return parts.length ? <>{parts}</> : <>{text}</>;
  };

  const getSenderName = (msg: any) => { if (msg.senderId === currentUser.uid) return 'Tú'; return memberProfiles[msg.senderId]?.displayName || msg.senderName || 'Usuario'; };
  const getSenderPhoto = (msg: any) => { if (msg.senderId === currentUser.uid) return currentUser.photoURL; return memberProfiles[msg.senderId]?.photoURL || msg.senderPhoto; };

  const groupedMessages = messages.reduce((groups: any[], msg, i) => {
    const prev = messages[i - 1];
    const showDate = !prev || formatDate(msg.createdAt) !== formatDate(prev.createdAt);
    const showSender = isGroup && (!prev || prev.senderId !== msg.senderId || showDate);
    return [...groups, ...(showDate ? [{ type: 'date', date: formatDate(msg.createdAt), id: `date-${i}` }] : []), { ...msg, showSender }];
  }, []);

  const minDateTime = new Date();
  minDateTime.setMinutes(minDateTime.getMinutes() + 1);
  const minDate = minDateTime.toISOString().split('T')[0];

  if (!activeChat) {
    return (
      <div className="chat-window chat-window--empty">
        <div className="chat-empty-state">
          <div className="chat-empty-state__icon">💬</div>
          <h2>Selecciona un chat</h2>
          <p>Elige una conversación para empezar</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={chatWindowRef} className="chat-window" onContextMenu={e => e.preventDefault()}>
      {/* Header */}
      {/* Pixel boot sequence */}
      {isPixelTheme && <PixelBoot />}
      <div className="chat-header">
        <div className="chat-header__avatar" onClick={() => { if (!isGroup) setViewingProfile({ uid: activeChat.uid, displayName: activeChat.displayName, username: activeChat.username, photoURL: activeChat.photoURL, status: contactStatus?.status, statusText: contactStatus?.statusText }); }} style={{ cursor: !isGroup ? 'pointer' : 'default' }}>
          {isGroup ? (
            <div className="chat-header__group-avatar">
              {activeChat.photoURL ? <img src={activeChat.photoURL} alt="" /> : <Users size={20} />}
            </div>
          ) : (
            activeChat.photoURL ? <img src={activeChat.photoURL} alt="" /> : getInitials(activeChat.displayName)
          )}
          {!isGroup && contactOnline && !contactHideOnline && <span className="online-dot" />}
        </div>
        <div className="chat-header__info">
          <div className="chat-header__name">{activeChat.displayName}</div>
          <div className={`chat-header__status ${!isGroup && contactOnline && !contactHideOnline ? 'chat-header__status--online' : ''}`}>
            {isGroup
              ? <span className="group-members-count" onClick={() => setShowGroupInfo(!showGroupInfo)}>{activeChat.members?.length || 0} miembros</span>
              : isRecording ? <span className="typing-status">🎙️ grabando audio...</span>
              : isTyping ? <span className="typing-status">escribiendo...</span>
              : contactStatus?.statusText
                ? <span>{getStatusDot(contactStatus.status)} {contactStatus.statusText}</span>
                : contactHideOnline && contactHideLastSeen
                ? '🔒 Privado'
                : contactHideOnline
                ? contactHideLastSeen ? '🔒 Privado' : `visto ${formatLastSeen(contactLastSeen)}`
                : contactOnline
                ? 'En línea'
                : contactHideLastSeen ? '' : `visto ${formatLastSeen(contactLastSeen)}`
            }
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
          {scheduledMsgs.length > 0 && (
            <button className="sidebar__icon-btn" onClick={() => setShowScheduledList(!showScheduledList)} title="Mensajes programados" style={{ position: 'relative' }}>
              <Clock size={18} />
              <span className="unread-badge" style={{ position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, fontSize: '0.6rem' }}>{scheduledMsgs.length}</span>
            </button>
          )}
          {!isGroup && (
            <>
              <button className="sidebar__icon-btn" onClick={async () => { const callId = await startCall({ currentUser, remoteUser: { uid: activeChat.uid, displayName: activeChat.displayName, photoURL: activeChat.photoURL }, callType: 'audio', chatId: activeChat.chatId }); sendCallNotification(activeChat.uid, { callId, callerName: currentUser.displayName || 'Usuario', callType: 'audio' }); }} title="Llamada de voz">
                <Phone size={18} />
              </button>
              <button className="sidebar__icon-btn" onClick={async () => { const callId = await startCall({ currentUser, remoteUser: { uid: activeChat.uid, displayName: activeChat.displayName, photoURL: activeChat.photoURL }, callType: 'video', chatId: activeChat.chatId }); sendCallNotification(activeChat.uid, { callId, callerName: currentUser.displayName || 'Usuario', callType: 'video' }); }} title="Videollamada">
                <Video size={18} />
              </button>
            </>
          )}
          {isGroup && (
            <>
              <button className="sidebar__icon-btn" onClick={async () => { await startGroupCall({ currentUser, groupId: activeChat.chatId, groupName: activeChat.displayName, members: activeChat.members || [], memberProfiles: activeChat.memberProfiles || {}, callType: 'audio' }); }} title="Llamada grupal">
                <Phone size={18} />
              </button>
              <button className="sidebar__icon-btn" onClick={async () => { await startGroupCall({ currentUser, groupId: activeChat.chatId, groupName: activeChat.displayName, members: activeChat.members || [], memberProfiles: activeChat.memberProfiles || {}, callType: 'video' }); }} title="Videollamada grupal">
                <Video size={18} />
              </button>
            </>
          )}
          <button className="sidebar__icon-btn" onClick={() => setShowSearch(!showSearch)} title="Buscar">
            <Search size={18} />
          </button>
          <div style={{ position: 'relative' }}>
            <button className="sidebar__icon-btn" onClick={() => setShowChatMenu(m => !m)} title="Más opciones">
              <MoreVertical size={18} />
            </button>
            {showChatMenu && (
              <div className="ctx-menu" style={{ position: 'absolute', top: '110%', right: 0, minWidth: 210 }} onClick={e => e.stopPropagation()}>
                {/* Disappearing messages */}
                <button className="ctx-menu__item" onClick={() => setShowDisappearMenu(m => !m)}>
                  <Timer size={15} /> Mensajes temporales {disappearTimer ? `(${disappearTimer >= 86400 ? disappearTimer/86400+'d' : disappearTimer >= 3600 ? disappearTimer/3600+'h' : disappearTimer/60+'min'})` : ''}
                </button>
                {showDisappearMenu && (
                  <div style={{ padding: '0.25rem 0.75rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                    {[
                      { label: 'Desactivar', value: null },
                      { label: '1 minuto', value: 60 },
                      { label: '5 minutos', value: 300 },
                      { label: '1 hora', value: 3600 },
                      { label: '24 horas', value: 86400 },
                      { label: '7 días', value: 604800 },
                    ].map(opt => (
                      <button key={opt.label} className="ctx-menu__item" style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem', background: disappearTimer === opt.value ? 'var(--primary)' : 'none', color: disappearTimer === opt.value ? '#fff' : 'var(--text-main)', borderRadius: 8 }}
                        onClick={async () => {
                          await updateDoc(doc(db, 'chats', activeChat.chatId), { disappearTimer: opt.value });
                          // System message notifying both users
                          // const label = opt.value ? opt.label : 'desactivados';
                          await addDoc(collection(db, 'chats', activeChat.chatId, 'messages'), {
                            type: 'system',
                            text: `${currentUser.displayName || 'Alguien'} ${opt.value ? 'activó mensajes temporales: ' + opt.label : 'desactivó los mensajes temporales'}`,
                            createdAt: serverTimestamp(),
                            senderId: 'system',
                          });
                          setShowDisappearMenu(false);
                          setShowChatMenu(false);
                        }}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                {!isGroup && (
                  <>
                    <div className="ctx-menu__divider" />
                    <button className="ctx-menu__item ctx-menu__item--danger" onClick={isBlocked ? handleUnblock : handleBlock}>
                      <Ban size={15} /> {isBlocked ? 'Desbloquear' : 'Bloquear usuario'}
                    </button>
                  </>
                )}
                <div className="ctx-menu__divider" />
                <button className="ctx-menu__item ctx-menu__item--danger" onClick={handleDeleteChat} disabled={deletingChat}>
                  <Trash2 size={15} /> {deletingChat ? 'Eliminando...' : 'Borrar chat'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scheduled messages list */}
      {showScheduledList && scheduledMsgs.length > 0 && (
        <div className="scheduled-list">
          <div className="scheduled-list__title"><Clock size={13} /> Mensajes programados</div>
          {scheduledMsgs.map(msg => {
            const date = msg.scheduledAt?.toDate ? msg.scheduledAt.toDate() : new Date(msg.scheduledAt);
            return (
              <div key={msg.id} className="scheduled-item">
                <div className="scheduled-item__content">
                  <span className="scheduled-item__text" style={{ color: 'var(--text-main)', opacity: 1 }}>{truncate(msg.text, 40)}</span>
                  <span className="scheduled-item__time" style={{ color: 'var(--primary)' }}>{date.toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <button className="scheduled-item__delete" onClick={() => handleDeleteScheduled(msg.id)}><X size={13} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Group info panel */}
      {isGroup && showGroupInfo && (
        <div className="group-info-panel">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem 1rem 0', gap: '0.5rem' }}>
            <label style={{ position: 'relative', cursor: isAdmin ? 'pointer' : 'default' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '1.5rem', color: '#fff', fontWeight: 700 }}>
                {(groupData?.groupPhoto || activeChat.photoURL)
                  ? <img src={groupData?.groupPhoto || activeChat.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : '👥'}
              </div>
              {isAdmin && <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleGroupPhotoUpload(e.target.files[0]); }} />}
            </label>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-main)' }}>{activeChat.displayName}</div>
            {(groupData?.description || activeChat.description) && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', textAlign: 'center' }}>{groupData?.description || activeChat.description}</div>
            )}
            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{activeChat.members?.length || 0} miembros</div>
          </div>

          {isAdmin && (
            <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem 1rem 0' }}>
              <button className="modal__btn modal__btn--primary" style={{ flex: 1, fontSize: '0.78rem', padding: '0.4rem', justifyContent: 'center' }}
                onClick={() => setShowAddMember(true)}>+ Añadir</button>
            </div>
          )}

          <div className="group-info-panel__title" style={{ marginTop: '0.75rem' }}>Miembros</div>
          <div className="group-info-panel__list">
            {(activeChat.members || []).map((uid: string) => {
              const isMe = uid === currentUser.uid;
              const profile = isMe ? { displayName: currentUser.displayName || 'Tú', photoURL: currentUser.photoURL } : memberProfiles[uid];
              const memberIsAdmin = (groupData?.admins || []).includes(uid);
              return (
                <div key={uid} className="group-info-panel__member" style={{ justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div className="group-info-panel__avatar">
                      {profile?.photoURL ? <img src={profile.photoURL} alt="" /> : getInitials(profile?.displayName || 'U')}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>{profile?.displayName || 'Usuario'}{isMe ? ' (Tú)' : ''}</div>
                      {memberIsAdmin && <div style={{ fontSize: '0.7rem', color: 'var(--primary)' }}>Admin</div>}
                    </div>
                  </div>
                  {isAdmin && !isMe && (
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button title={memberIsAdmin ? 'Quitar admin' : 'Hacer admin'} onClick={() => handleToggleAdmin(uid)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: memberIsAdmin ? 'var(--primary)' : 'var(--text-dim)', padding: '0.2rem 0.4rem', borderRadius: 6 }}>
                        {memberIsAdmin ? '★' : '☆'}
                      </button>
                      <button title="Remover" onClick={() => handleRemoveMember(uid)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.75rem', padding: '0.2rem 0.4rem', borderRadius: 6 }}>✕</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ padding: '0.75rem 1rem 1rem' }}>
            <button onClick={handleLeaveGroup}
              style={{ width: '100%', background: 'none', border: '1px solid #ef4444', color: '#ef4444', borderRadius: 10, padding: '0.45rem', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif' }}>
              🚪 Salir del grupo
            </button>
          </div>
        </div>
      )}

      {/* Search bar */}
      {showSearch && (
        <div className="search-bar">
          <Search size={15} color="var(--text-dim)" />
          <input ref={searchInputRef} className="search-bar__input" placeholder="Buscar en mensajes..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearchNav('down'); if (e.key === 'Escape') setShowSearch(false); }} />
          {searchResults.length > 0 && <span className="search-bar__count">{searchIndex + 1}/{searchResults.length}</span>}
          {searchQuery && searchResults.length === 0 && <span className="search-bar__count" style={{ color: 'var(--text-dim)' }}>0</span>}
          <button className="search-bar__nav" onClick={() => handleSearchNav('up')} disabled={searchResults.length === 0}><ChevronUp size={15} /></button>
          <button className="search-bar__nav" onClick={() => handleSearchNav('down')} disabled={searchResults.length === 0}><ChevronDown size={15} /></button>
          <button className="search-bar__close" onClick={() => setShowSearch(false)}><X size={15} /></button>
        </div>
      )}

      {/* Pinned banner */}
      {pinnedMsgs.length > 0 && (
        <div className="pinned-banner" onClick={() => pinnedMsgs.length === 1 ? scrollToPinned(pinnedMsgs[0].id) : setShowPinnedList(p => !p)}>
          <Pin size={13} className="pinned-banner__icon" />
          <div className="pinned-banner__content">
            <span className="pinned-banner__label">{pinnedMsgs.length === 1 ? 'Mensaje fijado' : `${pinnedMsgs.length} mensajes fijados`}</span>
            <span className="pinned-banner__text">{truncate(pinnedMsgs[pinnedMsgs.length - 1].text || '', 60)}</span>
          </div>
          <button className="pinned-banner__close" onClick={async (e) => { e.stopPropagation(); await updateDoc(doc(db, 'chats', activeChat.chatId), { pinnedMessages: [] }); }}>
            <X size={14} />
          </button>
        </div>
      )}

      {showPinnedList && pinnedMsgs.length > 1 && (
        <div className="pinned-list" onClick={e => e.stopPropagation()}>
          <div className="pinned-list__header">
            <Pin size={14} />
            <span>Mensajes fijados ({pinnedMsgs.length})</span>
            <button onClick={() => setShowPinnedList(false)}><X size={14} /></button>
          </div>
          {[...pinnedMsgs].reverse().map((p: any) => (
            <div key={p.id} className="pinned-list__item" onClick={() => scrollToPinned(p.id)}>
              <Pin size={12} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <span className="pinned-list__text">{truncate(p.text || '', 80)}</span>
              <button className="pinned-list__unpin" onClick={async (e) => {
                e.stopPropagation();
                await updateDoc(doc(db, 'chats', activeChat.chatId), { pinnedMessages: pinnedMsgs.filter((pm: any) => pm.id !== p.id) });
              }}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Scroll-to-bottom FAB */}
      {!isAtBottom && (
        <button
          onClick={() => { const c = messagesContainerRef.current; if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' }); setIsAtBottom(true); setUnreadWhileAway(0); }}
          style={{ position: 'absolute', bottom: '80px', right: '18px', zIndex: 50, background: 'var(--primary)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.3)', flexDirection: 'column' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          {unreadWhileAway > 0 && <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#22c55e', color: '#fff', borderRadius: '99px', fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', minWidth: '18px', textAlign: 'center', lineHeight: '16px' }}>{unreadWhileAway}</span>}
        </button>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="chat-messages" onScroll={handleScroll} onClick={() => { setShowEmoji(false); setShowGif(false); setContextMenu(null); setReactionPicker(null); setShowGroupInfo(false); setShowScheduledList(false); setShowChatMenu(false); setShowAttachMenu(false); setShowPinnedList(false); }}>
        {/* Load more indicator */}
        {loadingMore && (
          <div style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.78rem', color: 'var(--text-dim)' }}>
            Cargando mensajes...
          </div>
        )}
        {!loadingMore && hasMore && messages.length > 0 && (
          <div style={{ textAlign: 'center', padding: '0.5rem' }}>
            <button onClick={loadMoreMessages} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>
              ↑ Cargar más mensajes
            </button>
          </div>
        )}
        {groupedMessages.map((item) => {
          if (item.type === 'date') return <div key={item.id} className="chat-date-divider">{item.date}</div>;
          if ((item as any).type === 'system') return (
            <div key={item.id} style={{ textAlign: 'center', padding: '0.25rem 0' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', background: 'var(--input-bg)', borderRadius: 99, padding: '0.2rem 0.75rem' }}>{(item as any).text}</span>
            </div>
          );

          const isOut = (item as any).senderId === currentUser.uid;
          const emojiOnly = item.type === 'text' && isEmojiOnly(item.text || '');
          const reactions = item.reactions || {};
          const reactionEntries = Object.entries(reactions).filter(([, uids]: any) => uids.length > 0);
          const isPinned = pinnedMsgs.some((p: any) => p.id === item.id);
          const isSearchMatch = searchResults.includes(item.id);
          const isCurrentSearch = searchResults[searchIndex] === item.id;
          const senderName = getSenderName(item);
          const senderPhoto = getSenderPhoto(item);

          return (
            <div
              key={item.id}
              ref={el => { msgRefs.current[item.id] = el; }}
              className={`msg ${isOut ? 'msg--out' : 'msg--in'} ${isPinned ? 'msg--pinned' : ''} ${isCurrentSearch ? 'msg--search-active' : isSearchMatch ? 'msg--search-match' : ''} ${isGroup ? 'msg--group' : ''}`}
              onContextMenu={(e) => handleContextMenu(e, item)}
            >
              {isGroup && !isOut && item.showSender && (
                <div className="msg__group-avatar" style={{ cursor: 'pointer' }} onClick={() => {
                  const uid = item.senderId;
                  const profile = memberProfiles[uid];
                  if (profile) setViewingProfile({ uid, ...profile });
                }}>
                  {senderPhoto ? <img src={senderPhoto} alt="" /> : getInitials(senderName)}
                </div>
              )}
              {isGroup && !isOut && !item.showSender && <div className="msg__group-avatar-spacer" />}

              <div className="msg__content">
                {(item as any).forwarded && (
                  <div className="msg__forwarded">↪ Reenviado de {(item as any).forwardedFrom}</div>
                )}
                {isGroup && !isOut && item.showSender && (
                  <div className="msg__sender-name" style={{ color: `hsl(${Math.abs(item.senderId.charCodeAt(0) * 15) % 360}, 65%, 60%)` }}>
                    {senderName}
                  </div>
                )}

                {item.replyTo && (
                  <div className={`msg__reply-preview ${isOut ? 'msg__reply-preview--out' : ''}`}>
                    <div className="msg__reply-bar" />
                    <span className="msg__reply-text">
                      {item.replyTo.senderId === currentUser.uid ? 'Tú' : (item.replyTo.senderName || activeChat.displayName)}{': '}{truncate(item.replyTo.text || '', 60)}
                    </span>
                  </div>
                )}

                {item.type === 'text' && (
                  item.deleted ? (
                    <div className="msg__bubble msg__bubble--deleted">
                      🚫 Mensaje eliminado
                    </div>
                  ) : editingMsg?.id === item.id ? (
                    <div className="msg__edit-wrap">
                      <textarea className="msg__edit-input" value={editText} onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); } if (e.key === 'Escape') setEditingMsg(null); }} autoFocus />
                      <div className="msg__edit-actions">
                        <button className="msg__edit-btn msg__edit-btn--cancel" onClick={() => setEditingMsg(null)}>✕</button>
                        <button className="msg__edit-btn msg__edit-btn--save" onClick={handleSaveEdit}>✓</button>
                      </div>
                    </div>
                  ) : (
                    <div className={`msg__bubble ${emojiOnly ? 'msg__emoji-only' : ''}`}>
                      {isPinned && <Pin size={10} style={{ marginRight: '4px', opacity: 0.6 }} />}
                      {item.scheduled && <Clock size={10} style={{ marginRight: '4px', opacity: 0.6 }} />}
                      {searchQuery && isSearchMatch ? highlightText(item.text, searchQuery) : renderText(item.text || '', memberProfiles)}
                      {item.edited && <span className="msg__edited"> (editado)</span>}
                      {item.linkPreview && <MsgLinkPreview preview={item.linkPreview} isOut={isOut} />}
                    </div>
                  )
                )}
                {item.type === 'gif' && <div className="msg__gif"><img src={item.gifUrl} alt="GIF" /></div>}
                {item.type === 'image' && <div className="msg__gif" style={{ cursor: 'zoom-in' }} onClick={() => setViewingPhoto(item.imageUrl)}><img src={item.imageUrl} alt="Imagen" /></div>}
                {item.type === 'location' && (
                  <LocationMap lat={item.lat} lng={item.lng} live={item.live} isOut={isOut} />
                )}
                {item.type === 'location_end' && (
                  <div className='msg__bubble' style={{ fontSize: '0.82rem', opacity: 0.7 }}>📍 Dejó de compartir ubicación</div>
                )}
                {item.type === 'file' && (
                  <div className="msg__file">
                    <div className="msg__file-icon">
                      <FileText size={22} />
                    </div>
                    <div className="msg__file-info">
                      <span className="msg__file-name">{item.fileName}</span>
                      <span className="msg__file-size">{item.fileSize ? (item.fileSize / 1024 < 1024 ? (item.fileSize / 1024).toFixed(1) + ' KB' : (item.fileSize / 1024 / 1024).toFixed(1) + ' MB') : ''}</span>
                    </div>
                    <a href={item.fileData} download={item.fileName} className="msg__file-download" onClick={e => e.stopPropagation()}>
                      ⬇️
                    </a>
                  </div>
                )}
                {item.type === 'poll' && (() => {
                  const totalVotes = item.pollOptions?.reduce((s: number, o: any) => s + (o.votes?.length || 0), 0) || 0;
                  const userVote = item.pollOptions?.findIndex((o: any) => o.votes?.includes(currentUser.uid));
                  return (
                    <div className="msg__poll">
                      <div className="msg__poll-question">📊 {item.pollQuestion}</div>
                      {item.pollOptions?.map((opt: any, i: number) => {
                        const pct = totalVotes > 0 ? Math.round((opt.votes?.length || 0) / totalVotes * 100) : 0;
                        const voted = userVote === i;
                        return (
                          <button key={i} className={`msg__poll-option ${voted ? 'msg__poll-option--voted' : ''}`}
                            onClick={() => handlePollVote(item, i)}>
                            <div className="msg__poll-bar" style={{ width: `${pct}%` }} />
                            <span className="msg__poll-text">{opt.text}</span>
                            <span className="msg__poll-pct">{pct}%</span>
                          </button>
                        );
                      })}
                      <div className="msg__poll-total">{totalVotes} {totalVotes === 1 ? 'voto' : 'votos'}</div>
                    </div>
                  );
                })()}
                {item.type === 'call' && (() => {
                  const isOut = item.senderId === currentUser.uid;
                  const missed = item.status === 'missed' && !isOut;
                  const fmt = (s: number) => { const m = Math.floor(s/60); return `${m}:${(s%60).toString().padStart(2,'0')}`; };
                  return (
                    <div className="msg__bubble msg__call" style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem 0.75rem', opacity: missed ? 0.7 : 1,
                      background: missed ? 'color-mix(in srgb, #ef4444 15%, var(--card-bg))' : undefined,
                    }}>
                      <span style={{ fontSize: '1.1rem' }}>
                        {item.callType === 'video' ? '📹' : '📞'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: missed ? '#ef4444' : 'var(--text-main)' }}>
                          {missed ? 'Llamada perdida' : isOut ? 'Llamada saliente' : 'Llamada entrante'}
                        </div>
                        {item.duration > 0 && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>
                            {item.callType === 'video' ? 'Video · ' : ''}{fmt(item.duration)}
                          </div>
                        )}
                      </div>
                      {missed ? '📵' : '✅'}
                    </div>
                  );
                })()}
                {item.type === 'audio' && (
                  <div className={`msg__audio ${isOut ? 'msg__audio--out' : ''}`}>
                    <audio controls src={item.audioData} className="msg__audio-player" />
                    {item.duration && <span className="msg__audio-duration">{formatDuration(item.duration)}</span>}
                  </div>
                )}

                <div className="msg__meta">
                  <span className="msg__time">{formatTime(item.createdAt)}</span>
                  {isOut && (() => {
                    const seenBy: string[] = item.seenBy || [];
                    const otherMembers = (activeChat.members || [activeChat.uid]).filter((uid: string) => uid !== currentUser.uid);
                    const allSeen = otherMembers.length > 0 && otherMembers.every((uid: string) => seenBy.includes(uid));
                    const someSeen = seenBy.some((uid: string) => uid !== currentUser.uid);
                    const color = allSeen ? '#7c3aed' : someSeen ? '#a78bfa' : 'rgba(255,255,255,0.4)';
                    return (
                      <span className="msg__checks" style={{ color, position: 'relative', cursor: isGroup ? 'pointer' : 'default' }}
                        onClick={e => { e.stopPropagation(); if (isGroup) setSeenTooltip(seenTooltip === item.id ? null : item.id); }}>
                        <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                          <path d="M1 5.5L4.5 9L10 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5 5.5L8.5 9L14 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {isGroup && seenTooltip === item.id && (
                          <div className="seen-tooltip" onClick={e => e.stopPropagation()}>
                            <div className="seen-tooltip__title">Visto por ({seenBy.filter((uid: string) => uid !== currentUser.uid).length}/{otherMembers.length})</div>
                            {seenBy.filter((uid: string) => uid !== currentUser.uid).length === 0
                              ? <div className="seen-tooltip__empty">Nadie lo ha visto aún</div>
                              : seenBy.filter((uid: string) => uid !== currentUser.uid).map((uid: string) => {
                                  const profile = activeChat.memberProfiles?.[uid];
                                  return (
                                    <div key={uid} className="seen-tooltip__user">
                                      <div className="seen-tooltip__avatar">
                                        {profile?.photoURL ? <img src={profile.photoURL} alt="" /> : (profile?.displayName || 'U').slice(0, 2).toUpperCase()}
                                      </div>
                                      <span>{profile?.displayName || 'Usuario'}</span>
                                    </div>
                                  );
                                })
                            }
                          </div>
                        )}
                      </span>
                    );
                  })()}
                </div>

                {reactionEntries.length > 0 && (
                  <div className={`msg__reactions ${isOut ? 'msg__reactions--out' : ''}`}>
                    {reactionEntries.map(([emoji, uids]: any) => (
                      <button key={emoji} className={`reaction-pill ${uids.includes(currentUser.uid) ? 'reaction-pill--active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleReaction(item, emoji); }}>
                        {emoji} {uids.length > 1 && <span>{uids.length}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {isGroup && typingUsers.length > 0 && (
          <div className="typing-indicator">
            <div className="typing-dots-bare">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            </div>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginLeft: '0.35rem' }}>
              {typingUsers.length === 1 ? `${typingUsers[0]} está escribiendo` : `${typingUsers.join(', ')} están escribiendo`}
            </span>
          </div>
        )}
        {isTyping && !isGroup && (
          <div className="msg msg--in">
            <div className="typing-dots-bare">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            </div>
          </div>
        )}
        {isRecording && !isGroup && (
          <div className="msg msg--in">
            <div className="typing-dots-bare" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{ fontSize: '0.95rem', animation: 'typingPulse 1s ease-in-out infinite' }}>🎙️</span>
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div ref={contextMenuRef} className="ctx-menu" style={{ top: contextMenu.y, left: contextMenu.x }} onClick={e => e.stopPropagation()}>
          <button className="ctx-menu__item" onClick={handleMenuReply}><Reply size={15} /> Responder</button>
          {contextMenu.msg.type === 'text' && !contextMenu.msg.deleted && <button className="ctx-menu__item" onClick={handleMenuCopy}><Copy size={15} /> {copied ? '¡Copiado!' : 'Copiar'}</button>}
          {!contextMenu.msg.deleted && <button className="ctx-menu__item" onClick={handleMenuReact}><SmileIcon size={15} /> Reaccionar</button>}
          <button className="ctx-menu__item" onClick={handleMenuPin}><Pin size={15} /> {pinnedMsgs.some((p: any) => p.id === contextMenu.msg.id) ? 'Desfijar' : 'Fijar'}</button>
          {!contextMenu.msg.deleted && <button className="ctx-menu__item" onClick={handleMenuForward}><Forward size={15} /> Reenviar</button>}
          {!contextMenu.msg.deleted && <button className="ctx-menu__item" onClick={handleMenuAgenda}><CalendarDays size={15} /> Agregar a agenda</button>}
          {contextMenu.msg.senderId === currentUser.uid && contextMenu.msg.type === 'text' && !contextMenu.msg.deleted && (
            <button className="ctx-menu__item" onClick={handleMenuEdit}><Pencil size={15} /> Editar</button>
          )}
          {contextMenu.msg.senderId === currentUser.uid && !contextMenu.msg.deleted && (() => {
            const msgDate = contextMenu.msg.createdAt?.toDate ? contextMenu.msg.createdAt.toDate() : new Date(contextMenu.msg.createdAt);
            const canDeleteForAll = Date.now() - msgDate.getTime() < 24 * 60 * 60 * 1000;
            return (
              <>
                <div className="ctx-menu__divider" />
                {canDeleteForAll && (
                  <button className="ctx-menu__item ctx-menu__item--danger" onClick={handleMenuDelete}>
                    <Trash2 size={15} /> Eliminar para todos
                  </button>
                )}
                <button className="ctx-menu__item ctx-menu__item--danger" onClick={async () => {
                  if (!activeChat) return;
                  const hiddenFor = contextMenu!.msg.hiddenFor || [];
                  if (!hiddenFor.includes(currentUser.uid)) {
                    await updateDoc(doc(db, 'chats', activeChat.chatId, 'messages', contextMenu!.msg.id), {
                      hiddenFor: [...hiddenFor, currentUser.uid],
                    });
                  }
                  setContextMenu(null);
                }}>
                  <Trash2 size={15} /> Eliminar para mí
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Agenda from message modal */}
      {showAgendaFromMsg && (
        <div className="modal-overlay" onClick={() => setShowAgendaFromMsg(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>📅 Agregar a agenda</p>
              <button className="sidebar__icon-btn" onClick={() => setShowAgendaFromMsg(false)}><X size={18} /></button>
            </div>
            <input
              className="modal__input"
              value={agendaMsgText}
              onChange={e => setAgendaMsgText(e.target.value)}
              placeholder="Título del evento..."
              style={{ marginBottom: '0.75rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input type="date" className="modal__input" value={agendaDate} onChange={e => setAgendaDate(e.target.value)} style={{ flex: 1, marginBottom: 0 }} />
              <input type="time" className="modal__input" value={agendaTime} onChange={e => setAgendaTime(e.target.value)} style={{ flex: 1, marginBottom: 0 }} />
            </div>
            <button
              className="modal__btn modal__btn--primary"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleSaveAgendaFromMsg}
              disabled={agendaSaving || !agendaMsgText.trim() || !agendaDate || !agendaTime}
            >
              {agendaSaving ? 'Guardando...' : '✓ Guardar en agenda'}
            </button>
          </div>
        </div>
      )}

      {/* Reaction Picker */}
      {reactionPicker && (
        <ReactionPicker
          position={{ x: reactionPicker.x, y: reactionPicker.y }}
          onReact={(emoji) => handleReaction(reactionPicker.msg, emoji)}
          onClose={() => setReactionPicker(null)}
        />
      )}

      {/* Emoji Picker — debajo de la barra, sin portal */}
      {showEmoji && (
        <div className="ep-wrapper">
          <EmojiPickerCustom
            onEmojiClick={(emoji: string) => {
              setText(prev => prev + emoji);
              textareaRef.current?.focus();
            }}
            onClose={() => setShowEmoji(false)}
          />
        </div>
      )}

      {/* GIF Picker */}
      {showGif && <GifPicker onSelect={(url) => sendMessage('gif', url)} onClose={() => setShowGif(false)} />}

      {/* Reply bar */}
      {replyTo && (
        <div className="reply-bar">
          <div className="reply-bar__indicator" />
          <div className="reply-bar__content">
            <span className="reply-bar__who">{replyTo.senderId === currentUser.uid ? 'Tú' : (replyTo.senderName || activeChat.displayName)}</span>
            <span className="reply-bar__text">{truncate(replyTo.text || (replyTo.type === 'gif' ? '🎬 GIF' : '📷 Imagen'), 80)}</span>
          </div>
          <button className="reply-bar__close" onClick={() => setReplyTo(null)}><X size={16} /></button>
        </div>
      )}

      {/* Schedule modal */}
      {showSchedule && (
        <div className="modal-overlay" onClick={() => setShowSchedule(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>⏰ Programar mensaje</p>
              <button className="sidebar__icon-btn" onClick={() => setShowSchedule(false)}><X size={18} /></button>
            </div>
            <textarea
              className="msg__edit-input"
              style={{ width: '100%', marginBottom: '0.75rem', minHeight: 80 }}
              placeholder="Escribe tu mensaje..."
              value={scheduleText}
              onChange={e => setScheduleText(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input type="date" className="schedule-input" value={scheduleDate} min={minDate}
                onChange={e => setScheduleDate(e.target.value)} />
              <input type="time" className="schedule-input" value={scheduleTime}
                onChange={e => setScheduleTime(e.target.value)} />
            </div>
            <button className="modal__btn modal__btn--primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleScheduleMessage} disabled={!scheduleText.trim() || !scheduleDate || !scheduleTime}>
              ⏰ Programar
            </button>
          </div>
        </div>
      )}

      {/* Audio preview bar */}
      {audioPreview && (
        <div className="audio-preview-bar">
          <Mic size={16} color="var(--primary)" />
          <audio controls src={audioPreview.url} className="audio-preview-bar__player" />
          <span className="audio-preview-bar__duration">{formatDuration(recordingTime)}</span>
          <button className="reply-bar__close" onClick={cancelRecording}><X size={16} /></button>
          <button className="input-bar__send" onClick={sendVoiceMessage} disabled={sending}><Send size={16} /></button>
        </div>
      )}

      {/* Recording bar */}
      {recording && (
        <div className="recording-bar">
          <span className="recording-bar__dot" />
          <span className="recording-bar__time">{formatDuration(recordingTime)}</span>
          <span className="recording-bar__label">Grabando...</span>
          <button className="reply-bar__close" style={{ marginLeft: 'auto' }} onClick={cancelRecording}><X size={16} /></button>
          <button className="input-bar__send" onClick={stopRecording}><Square size={16} /></button>
        </div>
      )}

      {/* Blocked banner */}
      {isBlocked && !isGroup && (
        <div style={{ padding: '0.75rem 1.25rem', background: 'rgba(241,92,109,0.1)', borderTop: '1px solid var(--glass-border)', textAlign: 'center', fontSize: '0.85rem', color: '#f15c6d' }}>
          🚫 Bloqueaste a este usuario. <button onClick={handleUnblock} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Desbloquear</button>
        </div>
      )}

      {/* Link Preview while composing */}
      {linkPreview && !linkLoading && (
        <InputLinkPreview preview={linkPreview} onDismiss={dismissLink} />
      )}

      {/* Input bar */}
      {!recording && !audioPreview && (
        <div className="input-bar-wrap">
          {/* Format toolbar */}
          <div className="fmt-toolbar">
            <button className="fmt-btn" onMouseDown={e => { e.preventDefault(); applyFormat('*'); }} title="Negrita (Ctrl+B)"><b>B</b></button>
            <button className="fmt-btn fmt-btn--italic" onMouseDown={e => { e.preventDefault(); applyFormat('_'); }} title="Cursiva (Ctrl+I)"><i>I</i></button>
            <button className="fmt-btn" onMouseDown={e => { e.preventDefault(); applyFormat('~'); }} title="Tachado" style={{ textDecoration: 'line-through' }}>S</button>
            <button className="fmt-btn" onMouseDown={e => { e.preventDefault(); applyFormat('`'); }} title="Código" style={{ fontFamily: 'monospace' }}>{'{}'}</button>
          </div>
        <div className="input-bar">
          <button ref={emojiBtnRef} className="input-bar__btn" onClick={() => {
  setShowEmoji(prev => !prev);
  setShowGif(false);
  setShowAttachMenu(false);
}}><Smile size={22} /></button>
          <button className="input-bar__btn" onClick={() => { setShowGif(!showGif); setShowEmoji(false); }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)' }}>GIF</span>
          </button>
          <div style={{ position: 'relative' }}>
            <button className="input-bar__btn" onClick={() => { setShowAttachMenu(a => !a); setShowEmoji(false); setShowGif(false); }}>
              <Paperclip size={22} />
            </button>
            <AttachMenu
              isGroup={isGroup}
              sharingLocation={sharingLocation}
              visible={showAttachMenu}
              onImage={handleImageUpload}
              onVideo={(file) => handleDocUpload(file)}
              onDocument={handleDocUpload}
              onLocation={handleShareLocation}
              onPoll={() => { setShowPollCreator(true); }}
              onClose={() => setShowAttachMenu(false)}
            />
          </div>
          {/* Mention dropdown */}
          {showMentions && mentionList.length > 0 && (
            <div className="mention-dropdown">
              {mentionList.map((m: any) => (
                <button key={m.uid} className="mention-dropdown__item" onMouseDown={() => handleSelectMention(m)}>
                  <div className="mention-dropdown__avatar">
                    {m.photoURL ? <img src={m.photoURL} alt="" /> : getInitials(m.displayName)}
                  </div>
                  <span>{m.displayName}</span>
                </button>
              ))}
            </div>
          )}
          <textarea ref={textareaRef} className="input-bar__field" placeholder={isPixelTheme ? "█ ESCRIBE..." : "Escribe un mensaje..."} value={text}
            onChange={e => handleTextChange(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste} rows={1}
            onFocus={() => { setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 300); }} />
          <button className="input-bar__btn" onClick={() => { setShowSchedule(true); setScheduleText(text); }} title="Programar mensaje">
            <Clock size={20} />
          </button>
          <button className="input-bar__send" onClick={text.trim() ? handleSend : startRecording} disabled={sending}>
            {text.trim() ? <Send size={18} /> : <Mic size={18} />}
          </button>
        </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="modal-overlay" onClick={() => setShowAddMember(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>Añadir miembro</p>
              <button className="sidebar__icon-btn" onClick={() => setShowAddMember(false)}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <input className="modal__input" style={{ margin: 0, flex: 1 }} placeholder="@username..." value={addMemberInput}
                onChange={e => setAddMemberInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchAddMember()} />
              <button className="modal__btn modal__btn--primary" style={{ padding: '0 1rem' }} onClick={handleSearchAddMember} disabled={addMemberLoading}>
                {addMemberLoading ? '...' : 'Buscar'}
              </button>
            </div>
            {addMemberError && <p className="modal__error">{addMemberError}</p>}
            {addMemberResult && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'var(--input-bg)', borderRadius: 12, marginBottom: '0.75rem' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '0.85rem', color: '#fff', fontWeight: 700 }}>
                  {addMemberResult.photoURL ? <img src={addMemberResult.photoURL} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : getInitials(addMemberResult.displayName || addMemberResult.username)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{addMemberResult.displayName}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>@{addMemberResult.username}</div>
                </div>
                <button className="modal__btn modal__btn--primary" style={{ marginLeft: 'auto', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }} onClick={handleConfirmAddMember}>Añadir</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForward && forwardMsg && (
        <div className="modal-overlay" onClick={() => setShowForward(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p className="modal__title" style={{ margin: 0 }}>↪ Reenviar mensaje</p>
              <button className="sidebar__icon-btn" onClick={() => setShowForward(false)}><X size={18} /></button>
            </div>
            <div style={{ background: 'var(--input-bg)', borderRadius: 10, padding: '0.6rem 0.85rem', marginBottom: '1rem', fontSize: '0.82rem', color: 'var(--text-dim)', borderLeft: '3px solid var(--primary)' }}>
              {forwardMsg.type === 'text' ? forwardMsg.text?.slice(0, 80) + (forwardMsg.text?.length > 80 ? '...' : '') : `📎 ${forwardMsg.type}`}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginBottom: '0.75rem' }}>Selecciona un chat:</p>
            <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {(window as any).__tolkxChats?.filter((c: any) => c.chatId !== activeChat?.chatId).map((chat: any) => (
                <button key={chat.chatId} onClick={() => handleConfirmForward(chat)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: 12, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-main)', fontFamily: 'DM Sans, sans-serif', textAlign: 'left', transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '0.85rem', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                    {chat.photoURL ? <img src={chat.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getInitials(chat.displayName)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{chat.displayName}</div>
                    {chat.isGroup && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{chat.members?.length} miembros</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Profile Viewer */}
      {showPollCreator && (
        <div className="modal-overlay" onClick={() => setShowPollCreator(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)' }}>📊 Nueva encuesta</span>
              <button className="sidebar__icon-btn" onClick={() => setShowPollCreator(false)}><X size={18} /></button>
            </div>
            <input
              className="modal-input"
              placeholder="Pregunta..."
              value={pollQuestion}
              onChange={e => setPollQuestion(e.target.value)}
              style={{ marginBottom: '0.75rem' }}
            />
            {pollOptions.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  className="modal-input"
                  placeholder={`Opción ${i + 1}`}
                  value={opt}
                  onChange={e => { const o = [...pollOptions]; o[i] = e.target.value; setPollOptions(o); }}
                  style={{ flex: 1 }}
                />
                {pollOptions.length > 2 && (
                  <button className="sidebar__icon-btn" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}><X size={14} /></button>
                )}
              </div>
            ))}
            {pollOptions.length < 6 && (
              <button onClick={() => setPollOptions([...pollOptions, ''])}
                style={{ background: 'none', border: '1px dashed var(--glass-border)', borderRadius: 10, padding: '0.4rem 1rem', color: 'var(--text-dim)', cursor: 'pointer', width: '100%', marginBottom: '1rem', fontSize: '0.85rem' }}>
                + Agregar opción
              </button>
            )}
            <button onClick={handleSendPoll} disabled={sending}
              style={{ width: '100%', padding: '0.7rem', borderRadius: 12, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              Enviar encuesta
            </button>
          </div>
        </div>
      )}

      {viewingProfile && (
        <div className="profile-panel-overlay" onClick={() => setViewingProfile(null)}>
          <div className="profile-panel" onClick={e => e.stopPropagation()}>
            <div className="profile-panel__cover">
              <button className="profile-panel__close" onClick={() => setViewingProfile(null)}><X size={18} /></button>
              <div className="profile-panel__avatar"
                style={{ cursor: viewingProfile.photoURL ? 'zoom-in' : 'default' }}
                onClick={() => viewingProfile.photoURL && setViewingPhoto(viewingProfile.photoURL)}>
                {viewingProfile.photoURL
                  ? <img src={viewingProfile.photoURL} alt="" />
                  : <span>{getInitials(viewingProfile.displayName)}</span>}
              </div>
            </div>
            <div className="profile-panel__body">
              <div className="profile-panel__name">{viewingProfile.displayName}</div>
              {viewingProfile.username && <div className="profile-panel__username">@{viewingProfile.username}</div>}
              <div className="profile-panel__status">
                {getStatusDot(viewingProfile.status || 'offline')}
                <span>{viewingProfile.statusText || getStatusLabel(viewingProfile.status || 'offline')}</span>
              </div>
              {viewingProfile.bio && <div className="profile-panel__bio">{viewingProfile.bio}</div>}
              <div className="profile-panel__actions">
                <button className="profile-panel__btn profile-panel__btn--primary" onClick={() => setViewingProfile(null)}>
                  💬 Enviar mensaje
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Before Send */}
      {/* Image Editor */}
      {showEditor && editorSrc && (
        <ImageEditor
          src={editorSrc}
          onConfirm={handleEditorConfirm}
          onCancel={() => { setShowEditor(false); setEditorSrc(null); }}
        />
      )}

      {/* Image Preview - send confirmation */}
      {imagePreview && !showEditor && (
        <div className="photo-lightbox" onClick={() => setImagePreview(null)}>
          <button className="photo-lightbox__close" onClick={() => setImagePreview(null)}><X size={22} /></button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }} onClick={e => e.stopPropagation()}>
            <img src={imagePreview.url} className="photo-lightbox__img" alt="" style={{ maxHeight: '65vh' }} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setEditorSrc(imagePreview.url); setShowEditor(true); }}
                style={{ padding: '0.6rem 1.5rem', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem' }}>
                ✏️ Editar
              </button>
              <button onClick={() => setImagePreview(null)}
                style={{ padding: '0.6rem 1.5rem', borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem' }}>
                Cancelar
              </button>
              <button onClick={handleConfirmImageSend} disabled={uploadingImage}
                style={{ padding: '0.6rem 1.5rem', borderRadius: 12, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 700 }}>
                Enviar 📷
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox with zoom */}
      {viewingPhoto && (
        <div className="photo-lightbox" onClick={() => { setViewingPhoto(null); setPhotoZoom(1); }}>
          <button className="photo-lightbox__close" onClick={() => { setViewingPhoto(null); setPhotoZoom(1); }}><X size={22} /></button>
          <div className="photo-lightbox__zoom-controls" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPhotoZoom(z => Math.max(0.5, z - 0.25))}>−</button>
            <span>{Math.round(photoZoom * 100)}%</span>
            <button onClick={() => setPhotoZoom(z => Math.min(4, z + 0.25))}>+</button>
            <button onClick={() => setPhotoZoom(1)}>↺</button>
          </div>
          <div style={{ overflow: 'auto', maxWidth: '90vw', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
            <img
              src={viewingPhoto}
              className="photo-lightbox__img"
              alt=""
              style={{ transform: `scale(${photoZoom})`, transformOrigin: 'center center', transition: 'transform 0.2s', cursor: photoZoom > 1 ? 'grab' : 'zoom-in', maxWidth: '90vw', maxHeight: '85vh' }}
              onWheel={e => { e.stopPropagation(); setPhotoZoom(z => Math.min(4, Math.max(0.5, z - e.deltaY * 0.001))); }}
              onClick={() => setPhotoZoom(z => z === 1 ? 2 : 1)}
            />
          </div>
        </div>
      )}

      {/* Group Call */}
      {activeCall && activeChat && !isGroup && (
        <VideoCall
          currentUser={currentUser}
          remoteUser={{ uid: activeChat.uid, displayName: activeChat.displayName, photoURL: activeChat.photoURL }}
          callType={activeCall.type}
          chatId={activeChat.chatId}
          isIncoming={false}
          onEnd={() => setActiveCall(null)}
        />
      )}
    </div>
  );
};